import { Box, Link, TableCell, Typography } from '@mui/material';
import { AccessCostCell } from './AccessCostCell';
import { MarginCell } from './MarginCell';
import { DeliveryCell } from './DeliveryCell';
import { WarningsList } from './WarningsList';
import { FreeFromCell, orderCardHref, ReserveColumnHeader, WorkReserveCell, WorkReserveHeader } from './suggestionColumns';
import { driverShiftText, timeTodayOrDate } from './driverShift';
import { driverAvailableFromText } from './driverAvailableFrom';
import { plNumber } from '../../shared/number';
import type { BusyWith, VehicleCandidate } from './suggestionsApi';
import { AccessDistanceCell } from './RouteFigureCell';
import { useFeature } from '../featureFlags/FeatureGate';
import type { RouteFigure } from './routeFigure';
import {
  AfterOrderLine,
  SortableLabel,
  WindowReserveCell,
  type RecommendationSort,
  type RecommendationSortKey,
} from './recommendationView';

/**
 * Komórki wiersza kandydata — wspólne dla listy „Rekomendowane pojazdy”
 * (`VehicleSuggestions`) i wariantów na karcie zlecenia (`VariantsSection`,
 * 06.10.2026: „przy wariancie te same wyliczenia, co przy dodawaniu z listy”).
 * Jedno miejsce, żeby obie tabele mówiły to samo tymi samymi słowami; nic
 * tu nie liczymy — tylko pokazujemy to, co przyszło z API.
 */

/** Liczba z jednostką i data nie łamią się nigdy — „+33,2 / h” czyta się jak dwie wartości. */
export const NOWRAP = { whiteSpace: 'nowrap' } as const;

/** Ile kolumn zajmują wyliczenia (`CandidateCalcHeaders` / `CandidateCalcCells`). */
export const CALC_COLUMNS = 6;

/**
 * Najmniejsze szerokości kolumn (px, z marginesem komórki 2 × 8 px) — patrz
 * `CandidateCalcHeaders`. Rachunek przy oknie 1366 px (tabela ma 1104 px,
 * 06.10.2026): przy zwykłych danych kolumny potrzebują razem ok. 1036 px,
 * przy najdłuższych naraz (model, nazwisko, pięciocyfrowy koszt, chip marży,
 * zajęty pojazd, „okolice: …” przy GPS) ok. 1070 px.
 */
export const ACCESS_MIN_WIDTH = 154;
export const NOTES_MIN_WIDTH = 180;

/** Druga linia oznaczenia zajętości — „do 09.10, 21:25” w jednym kawałku. */
const BUSY_UNTIL_STYLE = { display: 'block', ...NOWRAP } as const;

/**
 * „zajęty: zlecenie X do hh:mm” — numer jako link do karty zlecenia w nowej
 * karcie, godzina w czasie polskim (inny dzień — z datą). Pod godziną jej
 * źródło (12.10.2026, decyzja użytkownika): „(wg planu TMS)”, gdy plan TMS
 * przy punktach kończy zlecenie później niż szacunek, inaczej „(szacunek)”.
 * To tylko tekst — kolejność i miejsce na dole listy liczy szacunek.
 *
 * Dwie linie: „zajęty: zlecenie X”, pod nią „do hh:mm”. W jednej linii bez
 * łamania (do 06.10.2026) oznaczenie poszerzało kolumnę „Pojazd” ze 157 do
 * 314 px i tabela rekomendacji przewijała się w karcie o 178 px przy oknie
 * 1366 (e2e). Teraz łamie się tylko między słowami; numer zlecenia i „do …”
 * zostają w jednym kawałku.
 */
export function BusyMarker({ busy }: { busy: BusyWith }) {
  // Źródło pod godziną tylko z przełącznikiem `occupiedUntilTmsPlan`
  // (wersja „obecna / nowa”, 12.10.2026); bez niego — jak w 0.31.3.
  const showSource = useFeature('occupiedUntilTmsPlan');
  const label = busy.externalId ?? (busy.userOfferId === null ? null : `#${busy.userOfferId}`);
  return (
    <Typography
      variant="caption"
      color="warning.main"
      data-testid="busy-marker"
      sx={{ display: 'block' }}
    >
      {'zajęty'}
      {label !== null && (
        <>
          {': zlecenie '}
          {busy.userOfferId !== null ? (
            <Link
              href={orderCardHref(busy.userOfferId)}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="inherit"
              sx={NOWRAP}
            >
              {label}
            </Link>
          ) : (
            <span style={NOWRAP}>{label}</span>
          )}
        </>
      )}
      {/* Spacja przed linią „do …” zostaje w tekście (czytnik ekranu,
          kopiowanie) — w bloku na początku linii i tak jej nie widać. */}
      {busy.until && (
        <>
          {' '}
          <span style={BUSY_UNTIL_STYLE} data-testid="busy-until">
            {`do ${timeTodayOrDate(busy.until)}`}
          </span>
          {showSource && (
            <>
              {' '}
              <span style={BUSY_UNTIL_STYLE} data-testid="busy-until-source">
                {busy.untilSource === 'tmsPlan' ? '(wg planu TMS)' : '(szacunek)'}
              </span>
            </>
          )}
        </>
      )}
    </Typography>
  );
}

/** Model i typ w osobnych liniach bez łamania — razem „DAF CF 370FA · JUMBO TRUCK” rozpadało się na trzy. */
export function VehicleModelLines({
  makeAndModel,
  carType,
}: {
  makeAndModel: string | null;
  carType: string | null;
}) {
  return (
    <>
      {[makeAndModel, carType].filter(Boolean).map((t) => (
        <Typography
          key={t as string}
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', ...NOWRAP }}
        >
          {t}
        </Typography>
      ))}
    </>
  );
}

/**
 * Nagłówki kolumn wyliczeń — w tej samej kolejności co `CandidateCalcCells`.
 * `measuredAccess` — w tabeli jest choć jeden dolot z map HERE (06.10.2026):
 * nagłówek bez „(szacunek)”, a źródło podpisuje każda komórka
 * (`showAccessSource` w `CandidateCalcCells`), żeby szacunek nie wyglądał
 * jak trasa z map.
 */
export function CandidateCalcHeaders({
  measuredAccess = false,
  windowReserveColumn = false,
  sort,
  onSort,
}: {
  measuredAccess?: boolean;
  /**
   * Kolumna „Zapas do okna” przed „Zapasem czasu pracy” (15.10.2026,
   * przełącznik `recommendationFeasibility`) — tylko lista rekomendacji;
   * tabela wariantów zostaje bez niej.
   */
  windowReserveColumn?: boolean;
  /** Sortowanie kliknięciem nagłówka (`recommendationColumnSort`); bez `onSort` — zwykłe nagłówki. */
  sort?: RecommendationSort | null;
  onSort?: (key: RecommendationSortKey) => void;
}) {
  return (
    <>
      <TableCell>Kierowca</TableCell>
      {/* Najmniejsza szerokość tak, żeby „HERE · jazda ok. 15,5 h” (138 px)
          zwykle mieścił się w jednej linii; dłuższy podpis i tak się zawinie. */}
      <TableCell align="right" sx={{ ...NOWRAP, minWidth: ACCESS_MIN_WIDTH }}>
        <SortableLabel sortKey="access" sort={sort} onSort={onSort}>
          {measuredAccess ? 'Dolot' : 'Dolot (szacunek)'}
        </SortableLabel>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {/* Marża stoi pod kosztem dolotu — sortowanie po niej z podpisu. */}
          {onSort ? (
            <SortableLabel sortKey="margin" sort={sort} onSort={onSort}>
              koszt dolotu · marża
            </SortableLabel>
          ) : (
            'koszt dolotu'
          )}
        </Typography>
      </TableCell>
      <TableCell sx={NOWRAP}>
        <SortableLabel sortKey="freeFrom" sort={sort} onSort={onSort}>
          Wolny od
        </SortableLabel>
      </TableCell>
      {/* „Zapas do okna” i „Zapas czasu pracy” w jednej kolumnie, jeden pod
          drugim, jak koszt i marża pod dolotem (integracja Etapu 1,
          15.10.2026): osobna kolumna dokładała ~100 px i tabela nie mieściła
          się przy 1366 px (decyzja 08.10.2026). Każda liczba ma swój podpis,
          dymek i sortowanie. */}
      {windowReserveColumn ? (
        <TableCell align="right">
          <ReserveColumnHeader sort={sort} onSort={onSort} />
        </TableCell>
      ) : (
        <TableCell align="right" data-testid="work-reserve-header">
          <SortableLabel sortKey="workReserve" sort={sort} onSort={onSort}>
            <WorkReserveHeader />
          </SortableLabel>
        </TableCell>
      )}
      {/* Nagłówek może zejść do dwóch linii („Rozładunek” / „(szacunek)”):
          w jednej trzymał kolumnę na 165 px przy wartościach na ~90 px, a tych
          70 px brakowało tabeli przy oknie 1366 px (e2e, 06.10.2026). */}
      <TableCell data-testid="delivery-header">
        <SortableLabel sortKey="delivery" sort={sort} onSort={onSort}>
          Rozładunek (szacunek)
        </SortableLabel>
      </TableCell>
      {/* 180 px: przy 240 tabela prowadzącego podział nie mieściła
          się w oknie 1366 px (02.10.2026) — uwagi łamią się na więcej linii.
          Przy 200 tabela z długim modelem, nazwiskiem i pięciocyfrową marżą
          naraz wychodziła o 8 px (pomiar 06.10.2026); przy zwykłych danych
          kolumna i tak dostaje ~230 px. */}
      <TableCell sx={{ minWidth: NOTES_MIN_WIDTH }}>Uwagi</TableCell>
    </>
  );
}

/** Kierowca, jego „dostępny od” (szacunek z limitów jazdy albo nieobecność) i zmiana z osi czasu tachografu. */
export function DriverCellContent({ candidate: k }: { candidate: VehicleCandidate }) {
  return (
    <>
      {k.driverName ?? '—'}
      {/* „Dostępny od" z limitów czasu jazdy to szacunek — podpisany
          („ok.”); z nieobecności w kartotece TMS to fakt — bez „ok.”.
          Obok dolotu, nigdy jako wykluczenie. */}
      {k.driverAvailability && (
        <Typography
          variant="caption"
          color={k.driverAvailability.availableNow ? 'text.secondary' : 'warning.main'}
          sx={{ display: 'block' }}
        >
          {k.driverAvailability.availableNow
            ? k.driverAvailability.remaining.todayH === null
              ? 'brak danych o czasie jazdy'
              : `dziś może jechać jeszcze ok. ${plNumber(k.driverAvailability.remaining.todayH)} h`
            : driverAvailableFromText(k.driverAvailability)}
        </Typography>
      )}
      {/* Zmiana z osi czasu tachografu (06.10.2026) — tylko
          gdy API ją podało; bez osi czasu nic nie dopisujemy. */}
      {k.driverAvailability?.shift && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="driver-shift"
          sx={{ display: 'block' }}
        >
          {driverShiftText(k.driverAvailability.shift)}
        </Typography>
      )}
    </>
  );
}

/**
 * Dolot z podpisanym źródłem (06.10.2026: z map HERE, szacunek poglądowo
 * w dymku; bez map albo w starszym API — szacunek jak dotąd,
 * `RouteFigureCell.tsx`), pod nim koszt dolotu i marża — w ich dymkach
 * opłaty drogowe z HERE. `showSource` — podpis źródła w komórce, gdy tabela
 * ma choć jeden dolot z map; `orderRoute` — trasa zlecenia do dymka marży.
 */
export function AccessCellContent({
  candidate: k,
  showSource = false,
  orderRoute,
}: {
  candidate: VehicleCandidate;
  showSource?: boolean;
  orderRoute?: RouteFigure | null;
}) {
  // `wrapSource` i `wrap` (06.10.2026, tabela przy oknie 1366 px): podpis
  // źródła łamie się po „·”, a marża przed procentem, chip pasma schodzi pod
  // kwotę — w jednej linii wyznaczały szerokość kolumny dolotu. Liczby
  // w środku się nie łamią (te same opcje co w zleceniach dla pojazdu).
  return (
    <>
      <AccessDistanceCell
        route={k.accessRoute}
        legacy={k.accessDistance}
        showSource={showSource}
        wrapSource
      />
      {/* Koszt pod dolotem, nie w osobnej kolumnie: to składnik
          tego samego dojazdu, a osobna kolumna zabierała
          miejsce, przez które łamały się liczby obok. */}
      <AccessCostCell cost={k.accessCost} compact route={k.accessRoute} />
      {/* Marża i alert marży (01.10.2026) — pod kosztem, bo to
          ten sam rachunek; alert, nie ranking. */}
      <MarginCell margin={k.margin} access={k.accessRoute} route={orderRoute} wrap />
    </>
  );
}

/**
 * Sześć komórek wyliczeń kandydata: kierowca, dolot z kosztem i marżą,
 * „Wolny od”, zapas, rozładunek, uwagi. `cellSx` — styl komórki (np. bez
 * dolnej kreski w tabeli wariantów). `showAccessSource` i `orderRoute` —
 * patrz `AccessCellContent`.
 */
export function CandidateCalcCells({
  candidate: k,
  cellSx,
  showAccessSource = false,
  orderRoute,
  windowReserveColumn = false,
  tightThresholdH,
  showAfterOrder = false,
}: {
  candidate: VehicleCandidate;
  cellSx?: Record<string, unknown>;
  showAccessSource?: boolean;
  orderRoute?: RouteFigure | null;
  /** Kolumna „Zapas do okna” (15.10.2026) — jak w `CandidateCalcHeaders`. */
  windowReserveColumn?: boolean;
  /** Próg „ciasnego dolotu” (h) — kolor zapasu do okna. */
  tightThresholdH?: number | null;
  /** „Po zleceniu wolny …” pod rozładunkiem (15.10.2026, `recommendationDetails`). */
  showAfterOrder?: boolean;
}) {
  return (
    <>
      <TableCell sx={cellSx}>
        <DriverCellContent candidate={k} />
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        <AccessCellContent candidate={k} showSource={showAccessSource} orderRoute={orderRoute} />
      </TableCell>
      <TableCell sx={cellSx}>
        <FreeFromCell position={k.position} />
      </TableCell>
      {/* Zapas czasu pracy po zleceniu (06.10.2026) — liczba
          z dymkiem, jak liczona; brak danych to „—” z powodem. Z „Zapasem do
          okna” (15.10.2026) — ta sama kolumna, zapas do okna nad nim. */}
      {windowReserveColumn ? (
        <TableCell align="right" sx={cellSx}>
          <Box data-testid="window-reserve-cell">
            <WindowReserveCell reserve={k.windowReserve} thresholdH={tightThresholdH} />
          </Box>
          <Box data-testid="spare-cell">
            <WorkReserveCell reserve={k.workReserve} />
          </Box>
        </TableCell>
      ) : (
        <TableCell align="right" sx={cellSx} data-testid="spare-cell">
          <WorkReserveCell reserve={k.workReserve} />
        </TableCell>
      )}
      <TableCell sx={cellSx}>
        <DeliveryCell delivery={k.delivery} />
        {showAfterOrder && <AfterOrderLine afterOrder={k.afterOrder} />}
      </TableCell>
      <TableCell sx={cellSx}>
        <WarningsList warnings={k.ostrzezenia} />
      </TableCell>
    </>
  );
}
